import { type FC, useCallback, useLayoutEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { Text } from 'components'

import styles from './ServicesPage.module.scss'

const pillars = [
  { num: '01', title: 'projecting', text: 'projectingText' },
  { num: '02', title: 'installation', text: 'installationText' },
  { num: '03', title: 'bim', text: 'bimText' },
]

// Threshold (px) above the shortest block before a card counts as expandable —
// avoids showing "read more" for a one-line difference.
const EXPAND_THRESHOLD = 8

// Must match .pillar__text / .pillar__toggle line-height in the stylesheet. The
// collapsed card is sized to a whole number of these lines so text is never cut
// mid-line, and the toggle occupies exactly the last line.
const LINE_HEIGHT = 24

const Pillars: FC = () => {
  const { i18n } = useTranslation()
  const textRefs = useRef<Array<HTMLDivElement | null>>([])
  const [fullHeights, setFullHeights] = useState<number[]>([])
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null)
  const [expanded, setExpanded] = useState<boolean[]>(() => pillars.map(() => false))

  // Measure the inner paragraph's full content height (scrollHeight reports the
  // full text even while the wrapper clamps it), so resizes re-measure correctly.
  const measure = useCallback(() => {
    const heights = textRefs.current.map(wrap => {
      const p = wrap?.firstElementChild

      return p instanceof HTMLElement ? p.scrollHeight : 0
    })
    if (heights.some(h => h === 0)) return

    setFullHeights(heights)
    setCollapsedHeight(Math.min(...heights))
  }, [])

  // Re-measure on mount, on viewport resize and whenever the language changes
  // (text length — and therefore height — differs per locale).
  useLayoutEffect(() => {
    measure()

    // fonts loading after first paint changes text height — re-measure once ready
    document.fonts?.ready.then(measure)
    window.addEventListener('resize', measure)

    return () => window.removeEventListener('resize', measure)
  }, [measure, i18n.language])

  const toggle = (index: number) => setExpanded(prev => prev.map((v, i) => (i === index ? !v : v)))

  return (
    <div className={classNames('container', styles.pillars)}>
      {pillars.map(({ num, title, text }, index) => {
        const full = fullHeights[index] ?? 0
        const expandable = collapsedHeight != null && full > collapsedHeight + EXPAND_THRESHOLD
        const isExpanded = expanded[index]

        // collapse to a whole number of lines so nothing is cut mid-line; an
        // expandable card gives its last line to the toggle, keeping cards equal.
        const lineCount = collapsedHeight == null ? 0 : Math.round(collapsedHeight / LINE_HEIGHT)
        const collapsedCardHeight = lineCount * LINE_HEIGHT
        const height =
          collapsedHeight == null ? undefined : isExpanded ? full + LINE_HEIGHT + 4 : collapsedCardHeight
        const textMaxHeight =
          collapsedHeight == null || isExpanded
            ? undefined
            : expandable
              ? (lineCount - 1) * LINE_HEIGHT
              : collapsedCardHeight

        return (
          <article key={num} className={styles.pillar}>
            <span className={styles.pillar__num} aria-hidden>
              {num}
            </span>
            <Text className={styles.pillar__title} text={title} tagName='h2' />

            <div
              ref={el => {
                textRefs.current[index] = el
              }}
              className={styles.pillar__textWrap}
              style={{ height }}
            >
              <div className={styles.pillar__textClamp} style={{ maxHeight: textMaxHeight }}>
                <Text className={styles.pillar__text} text={text} />
              </div>
              {expandable && (
                <span
                  role='button'
                  tabIndex={0}
                  className={styles.pillar__toggle}
                  onClick={() => toggle(index)}
                  onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggle(index)}
                >
                  <Text text={isExpanded ? 'readLess' : 'readMore'} emptyTag />
                </span>
              )}
            </div>
          </article>
        )
      })}
    </div>
  )
}

export default Pillars
